import React from 'react';
import { useGetList, useRecordContext } from 'react-admin';
import { Box, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import {
	BillingAccountRow,
	PROJECT_STATUS_DELETE,
	ProjectUsageRow,
	projectBadges,
} from './billingAlertsState';
import { LastProcess, MeasuredAmount, StateChips } from './cells';
import { PeriodRange } from './PeriodFilter';
import { secondaryTableSx } from './columnLayout';

/** Un compte n'a jamais des centaines de projets : une page suffit. */
const PROJECTS_PER_ACCOUNT = 100;

// Les journées mesurées en légende, pas en colonne : un projet créé en cours de
// plage a un coût bas pour une bonne raison.
const projectCaption = (project: ProjectUsageRow): string =>
	[
		project.folderId,
		project.measuredDays
			? `${project.measuredDays} jour${project.measuredDays > 1 ? 's' : ''} mesuré${project.measuredDays > 1 ? 's' : ''}`
			: 'non mesuré',
		project.connectorCount
			? `${project.connectorCount} connecteur${project.connectorCount > 1 ? 's' : ''}`
			: 'aucun connecteur',
	].join(' · ');

// Ni rouge ni orange : ces deux couleurs appellent un geste, or managé n'est pas
// une alerte.
const ManagedWord: React.FC<{ managed: boolean }> = ({ managed }) => (
	<Typography
		component="span"
		fontSize="inherit"
		fontWeight={600}
		color={managed ? 'primary.main' : 'text.disabled'}
	>
		{managed ? 'managé' : 'non managé'}
	</Typography>
);

/**
 * Une ligne projet, calée sur les colonnes du tableau parent (columnLayout.ts) :
 * pas d'en-tête à elle, et des cellules vides pour les rangs qui n'ont rien à
 * dire d'un projet — abonnement et nombre de projets se lisent sur la ligne du
 * compte.
 */
const ProjectRow: React.FC<{ project: ProjectUsageRow }> = ({ project }) => (
	<TableRow>
		{/* Rang du dépliage : vide, il sert de gouttière au bloc déplié. */}
		<TableCell />
		<TableCell sx={{ pl: 3 }}>
			<Link
				to={`/projects/${project.id}`}
				style={{
					color: 'inherit',
					fontWeight: 600,
					textDecoration: project.status === PROJECT_STATUS_DELETE ? 'line-through' : 'none',
				}}
			>
				{project.name || '—'}
			</Link>
			<Typography
				variant="caption"
				color="text.secondary"
				display="block"
				sx={{ fontSize: '0.75rem' }}
			>
				<ManagedWord managed={project.managed} /> · {projectCaption(project)}
			</Typography>
		</TableCell>
		<TableCell />
		<TableCell />
		{/* Rang de la colonne « Dernier process », vide jusqu'ici : c'est la ligne
		    projet qui porte nativement ce signal, la ligne compte n'en est que
		    l'agrégat. */}
		<TableCell>
			<LastProcess at={project.lastProcessAt} />
		</TableCell>
		<TableCell align="right">
			<MeasuredAmount value={project.storageEur} measuredDays={project.measuredDays} />
		</TableCell>
		<TableCell align="right">
			<MeasuredAmount value={project.totalEur} measuredDays={project.measuredDays} />
		</TableCell>
		<TableCell>
			<StateChips badges={projectBadges(project)} />
		</TableCell>
	</TableRow>
);

/**
 * Le détail d'un billing account déplié : tous ses projets sur la même plage,
 * managés ET non managés. Un non managé pointe sur le datawarehouse du client et
 * ne coûte rien à Quanti — c'est justement ce qui distingue un compte coûteux
 * d'un compte gratuit pour nous.
 *
 * Une requête simple plutôt qu'une List react-admin imbriquée : deux List sur la
 * même page se disputent les paramètres d'URL, et celle-ci n'a besoin ni de
 * filtres, ni de tri, ni de pagination propres.
 */
export const AccountProjects: React.FC<{ range: PeriodRange }> = ({ range }) => {
	const account = useRecordContext<BillingAccountRow>();
	const { data, isLoading } = useGetList<ProjectUsageRow>('project-dw-usage', {
		pagination: { page: 1, perPage: PROJECTS_PER_ACCOUNT },
		sort: { field: 'totalEur', order: 'DESC' },
		filter: { billingAccountId: account?.id, from: range.from, to: range.to },
	});

	// Plus de regroupement managés/non managés : l'ordre est celui du back, la
	// plus grosse dépense d'abord. Les deux familles se lisent dans la même liste.
	const projects = data || [];

	if (isLoading) {
		return (
			<Typography sx={{ p: 2 }} color="text.secondary">
				Chargement des projets…
			</Typography>
		);
	}
	if (!projects.length) {
		return (
			<Typography sx={{ p: 2 }} color="text.secondary">
				Aucun projet sur ce compte.
			</Typography>
		);
	}

	return (
		// Aucun padding horizontal sur ce conteneur : il décalerait le tableau et
		// les lignes projet ne tomberaient plus sous les en-têtes du parent.
		<Box sx={{ backgroundColor: 'action.hover' }}>
			<Table size="small" sx={secondaryTableSx}>
				<TableBody>
					{projects.map((project) => (
						<ProjectRow key={project.id} project={project} />
					))}
				</TableBody>
			</Table>
		</Box>
	);
};
